// contexts/SetupContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { SetupProgress, MENU_PERMISSIONS } from "@/types/setup";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { SetupChecklistResponse } from "@/types/checklist";
import { convertChecklistToProgress } from "@/transforms/setupTransform";

interface SetupContextType {
  setupProgress: SetupProgress | null;
  isLoading: boolean;
  isPathAllowed: (path: string) => boolean;
  refreshSetupProgress: () => Promise<void>;
}

const SetupContext = createContext<SetupContextType | undefined>(undefined);

export const SetupProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [setupProgress, setSetupProgress] = useState<SetupProgress | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);

  const refreshSetupProgress = async () => {
    setIsLoading(true);
    try {
      const response = await ApiService.request<SetupChecklistResponse>(
        EndpointKeys.GET_EMPLOYER_CHECKLIST,
      );
      setSetupProgress(convertChecklistToProgress(response));
    } catch (error) {
      console.error("Failed to fetch setup progress:", error);
      // Set default values if fetch fails
      setSetupProgress({
        parentInformation: false,
        employerManagement: false,
        employeeManagement: false,
        pensions: false,
        earnings: false,
        timeOff: false,
        filings: false,
        payruns: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshSetupProgress();
  }, []);

  const isPathAllowed = (path: string): boolean => {
    // If still loading or no setup progress, only allow unrestricted paths
    if (isLoading || !setupProgress) {
      const permission = MENU_PERMISSIONS[path];
      return !permission?.requiresSetup;
    }

    const permission = MENU_PERMISSIONS[path];

    // If path doesn't require setup or isn't in permissions, allow it
    if (!permission || !permission.requiresSetup) {
      return true;
    }

    // Check if all required items are completed
    return (
      permission.requiredItems?.every((item) => setupProgress[item]) ?? true
    );
  };

  return (
    <SetupContext.Provider
      value={{ setupProgress, isPathAllowed, refreshSetupProgress, isLoading }}
    >
      {children}
    </SetupContext.Provider>
  );
};

export const useSetup = () => {
  const context = useContext(SetupContext);
  if (context === undefined) {
    throw new Error("useSetup must be used within a SetupProvider");
  }
  return context;
};
